import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Text, Content, Form, Row, Col, Grid, FlexboxGrid } from 'rsuite';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import {
  selectConfigExpressAdminUpdatePropertyDetailsQuestions,
  selectConfigExpressPropertyDetailsQuestions,
  selectConfigExpressRenewPropertyDetailsQuestions,
  selectConfigExpressUpdatePropertyDetailsQuestions,
} from '../../store/slices/config/configSelectors';
import { convertStringToKey } from '../../common/utils';
import { FLOWS_ENUM } from '../../common/enums';
import { selectUpdatedPermitData } from '../../store/slices/update/updateSelectors';
import { updatePermitData } from '../../store/slices/update/updateSlice';
import { FormQuestion } from 'src/components/form/FormQuestion/FormQuestion';

const UpdatePropertyDetails = () => {
  const dispatch = useDispatch();
  const form = useRef();

  const applyPropertyDetailsQuestions = useSelector(
    selectConfigExpressPropertyDetailsQuestions
  );
  const updatePropertyDetailsQuestions = useSelector(
    selectConfigExpressUpdatePropertyDetailsQuestions
  );
  const renewPropertyDetailsQuestions = useSelector(
    selectConfigExpressRenewPropertyDetailsQuestions
  );
  const adminUpdatePropertyDetailsQuestions = useSelector(
    selectConfigExpressAdminUpdatePropertyDetailsQuestions
  );

  // Get distinct supplemental questions from all flows
  // when we save we need to pass in all questions as part of the payload, otherwise they are deleted
  // (this includes admin-only questions - they're never rendered on this page, but their answers
  // must be carried forward here too, or this save wipes them out)
  // on this page we only want to display the update related supplemental questions
  const allPropertyDetailsQuestions = useMemo(() => {
    const allQuestions = [
      ...(applyPropertyDetailsQuestions || []),
      ...(updatePropertyDetailsQuestions || []),
      ...(renewPropertyDetailsQuestions || []),
      ...(adminUpdatePropertyDetailsQuestions || []),
    ];

    return allQuestions.reduce((acc, current) => {
      const existingIndex = acc.findIndex((item) => item.key === current.key);
      // If key does not exist add it else replace existing value
      if (existingIndex === -1) {
        acc.push(current);
      } else {
        acc[existingIndex] = current;
      }
      return acc;
    }, []);
  }, [
    applyPropertyDetailsQuestions,
    updatePropertyDetailsQuestions,
    renewPropertyDetailsQuestions,
    adminUpdatePropertyDetailsQuestions,
  ]);

  const { propertyDetails, property } = useSelector(selectUpdatedPermitData);

  const initialFormValues = { ...propertyDetails, ...property };

  const [formValues, setFormValues] = useState(initialFormValues);
  const [formError, setFormError] = useState({});

  useEffect(() => {
    setFormValues({ ...propertyDetails, ...property });
  }, [propertyDetails]);

  const formModel = useMemo(() => ({ ...formValues }), [formValues]);

  const onRadioQuestionGroupChanged = (value, _, questionKey) => {
    updateFormValues(questionKey, value);
  };

  const updateFormValues = (property, value) => {
    const currentValues = { ...formValues };
    currentValues[property] = value;
    setFormValues(currentValues);
  };

  const prepareSupplementalQuestionsObject = () => {
    let supplementalQuestions = {};
    for (const question of allPropertyDetailsQuestions) {
      supplementalQuestions[question.key] = {
        question: question.title,
        questionSubtext: question.description,
        answer: formValues[question.key] || null, // api call expects there to be an answer
        ref: question.key,
      };
    }
    return supplementalQuestions;
  };

  const updateApplicationData = () => {
    const supplementalQuestions = prepareSupplementalQuestionsObject();
    dispatch(
      updatePermitData({
        propertyDetails: {
          ...formValues,
          supplementalQuestions: {
            ...supplementalQuestions,
          },
        },
      })
    );
  };

  const dispatchPropertyDetails = () => {
    if (!form?.current?.check()) return false;
    updateApplicationData();
    return true;
  };

  const handleBackClick = () => {
    // don't validate on back click
    // it will be validated next time they go next on this page
    updateApplicationData();
    return true;
  };
  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.UPDATE_CERTIFICATE} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content as="div">
          <FlowPageLayout
            title="Property Details"
            onNext={dispatchPropertyDetails}
            onBack={handleBackClick}
          >
            <Grid fluid>
              <Row>
                <Col>
                  <Text className="mb-24">
                    All fields must be completed before proceeding.
                  </Text>
                </Col>
              </Row>

              <Row className="mt-12 mb-24">
                <Col xs={24}>
                  <Form
                    ref={form}
                    checkTrigger="none"
                    formDefaultValue={formModel}
                    formValue={formValues}
                    onCheck={setFormError}
                    onChange={setFormValues}
                    fluid
                  >
                    <div role="grid">
                      {updatePropertyDetailsQuestions?.map((question, idx) => {
                        return (
                          <Row
                            className="mb-24"
                            key={`question_${convertStringToKey(question.key)}_${idx}`}
                          >
                            <Col xs={24}>
                              <FormQuestion
                                question={question}
                                formValues={formValues}
                                formError={formError}
                                updateFormValues={updateFormValues}
                                onRadioQuestionGroupChanged={
                                  onRadioQuestionGroupChanged
                                }
                                initialFormValues={initialFormValues}
                              />
                            </Col>
                          </Row>
                        );
                      })}
                    </div>
                  </Form>
                </Col>
              </Row>
            </Grid>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default UpdatePropertyDetails;
